/**
 * impressionBuffer.ts
 *
 * Accumulates impression events in memory and flushes them as a single
 * batch INSERT every 30 seconds, or immediately when the tab is hidden.
 *
 * Two-layer dedup protection:
 * 1. Module-level seen Set in VenueCard.tsx prevents trackImpression
 *    from being called twice for the same venue in a session.
 * 2. This buffer deduplicates by (venue_id, event_type) before flushing
 *    as belt-and-suspenders against any observer race conditions.
 */

import { supabase } from '../lib/supabase'

export type ImpressionEventType = 'card_view' | 'detail_view'

interface ImpressionEvent {
  venue_id:   string
  event_type: ImpressionEventType
}

// Module-level buffer — survives component unmounts within a session
const buffer: ImpressionEvent[] = []

// Belt-and-suspenders dedup — catches any duplicate trackImpression calls
// that slip through the VenueCard-level seen Set
const buffered = new Set<string>()  // key: `${venue_id}:${event_type}`

// SESSION-LEVEL dedup — unlike `buffered` (which resets every flush window),
// this Set persists for the life of the JS runtime. One venue = one
// card_view + one detail_view per session, maximum. Without this,
// detail_view fired on every remount of VenueDetailPage, so one user
// tapping the same venue five times looked like five distinct views —
// which inflated Trending and every venue-facing stat built on it.
const sessionSeen = new Set<string>()

let initialized = false

async function flush(): Promise<void> {
  if (buffer.length === 0) return

  // Drain atomically before await so events aren't lost if flush is re-called
  const batch = buffer.splice(0, buffer.length)
  buffered.clear()  // reset dedup for next window

  try {
    await supabase.from('venue_impressions').insert(batch)
  } catch {
    // Impressions are analytics — never crash the app over them
  }
}

function init(): void {
  if (initialized) return
  initialized = true

  setInterval(flush, 30_000)

  // visibilitychange covers iOS Safari swipe-away (beforeunload is unreliable on iOS)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flush()
  })

  window.addEventListener('pagehide', () => { void flush() })
}

/**
 * Queue a venue impression event for batched delivery to Supabase.
 * Deduplicates by (venue_id, event_type) within each flush window.
 */
export function trackImpression(venueId: string, eventType: ImpressionEventType): void {
  if (!venueId) return

  const key = `${venueId}:${eventType}`
  if (sessionSeen.has(key)) return  // already counted this session
  if (buffered.has(key)) return     // deduplicate within current flush window
  sessionSeen.add(key)
  buffered.add(key)

  init()
  buffer.push({ venue_id: venueId, event_type: eventType })
}

/**
 * Force an immediate flush. Useful for testing or before critical navigation.
 */
export function flushImpressions(): Promise<void> {
  return flush()
}

/**
 * Has this venue/event already been counted this session?
 * VenueCard uses this as its observer guard — single source of dedup truth
 * lives here instead of a private Set in the component.
 */
export function hasTrackedImpression(venueId: string, eventType: ImpressionEventType): boolean {
  return sessionSeen.has(`${venueId}:${eventType}`)
}

/**
 * Start a fresh impression session. Called by AppContext when the app
 * resumes after a long background gap (native processes can live for days —
 * without this, a venue could only ever be counted once per process).
 */
export function resetImpressionSession(): void {
  sessionSeen.clear()
  buffered.clear()
}
